import { AfterRenderRef, Injector, Provider, Signal, WritableSignal, afterNextRender, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GROUP_STATE_PREFIX, UserAuthConfig as ClientUserAuthConfig } from '@roosevelt/client-ui-lib';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { getUserAuths } from '../login/store/selectors';

function hasAuthorizedPlan(
  userAuthorizations: Record<string, string[]> | undefined,
  selectedAdminPlan: string
): boolean {
  return !!selectedAdminPlan && !!userAuthorizations?.[selectedAdminPlan]?.length;
}

function clearClientSearchState(
  lux: Lux<any>,
  storagePrefix: string,
  selectedAdminPlan: string
): void {
  sessionStorage.removeItem(`${storagePrefix}client-search-state`);
  lux.set(state => state.clientSearch, {
    isFetchingClient: false,
    clientOrSubClientSearchErrors: '',
    selectedAdminPlan,
    clientsInfo: {
      clients: [],
      pagination: {
        offset: 0,
        limit: 25
      },
      sort: []
    },
    subClientInfo: {
      subClients: [],
      pagination: {
        offset: 0,
        limit: 25
      },
      sort: []
    },
    selectedSubClient: [],
    isSubClientCalled: false,
    subClientFlow: false
  });
}

function createClientSearchStateClearer(
  lux: Lux<any>,
  storagePrefix: string,
  selectedAdminPlan: () => string
): () => void {
  return () => clearClientSearchState(lux, storagePrefix, selectedAdminPlan());
}

export function provideClientSearchStatePrefix(storagePrefix: string): Provider {
  return {
    provide: GROUP_STATE_PREFIX,
    useValue: storagePrefix
  };
}

type ClientSearchRenderControllerOptions = {
  injector: Injector;
  clearState: () => void;
  canRender?: () => boolean;
  renderTrigger?: () => unknown;
  remountTrigger?: () => unknown;
  isActive?: () => boolean;
};

export type ClientSearchRenderController = {
  renderClientSearch: WritableSignal<boolean>;
  scheduleRender: (clearState: boolean) => void;
  destroy: () => void;
};

export type ManagedClientSearchController = ClientSearchRenderController & {
  clientSearchRenderState: Signal<'fetching' | 'idle'>;
};

type ClientSearchAuthorizationOptions = {
  injector: Injector;
  selectedAdminPlan: () => string;
  userAuths: () => Record<string, string[]>;
  clearState: () => void;
  remountTrigger: () => unknown;
  isActive?: () => boolean;
  canRenderWhenAuthorized?: () => boolean;
};

type ManagedClientSearchOptions = Omit<ClientSearchAuthorizationOptions, 'clearState'> & {
  lux: Lux<any>;
  storagePrefix: string;
  clientSearchFetching: () => boolean;
};

type FeatureManagedClientSearchOptions = Omit<ManagedClientSearchOptions, 'clientSearchFetching' | 'injector' | 'userAuths'>;

export function createClientSearchRenderController(
  options: ClientSearchRenderControllerOptions
): ClientSearchRenderController {
  const renderClientSearch = signal(false);
  const canRender = options.canRender ?? (() => true);
  const isActive = options.isActive ?? (() => true);

  let clientSearchRenderRef: AfterRenderRef | null = null;
  let clientSearchRenderVersion = 0;

  const clearScheduledClientSearchRender = (): void => {
    clientSearchRenderRef?.destroy();
    clientSearchRenderRef = null;
  };

  const scheduleRender = (clearState: boolean): void => {
    clearScheduledClientSearchRender();
    const currentRenderVersion = ++clientSearchRenderVersion;

    renderClientSearch.set(false);

    if (clearState) {
      options.clearState();
    }

    if (!canRender()) {
      return;
    }

    clientSearchRenderRef = afterNextRender(() => {
      clientSearchRenderRef = null;
      if (currentRenderVersion !== clientSearchRenderVersion || !canRender()) {
        return;
      }

      renderClientSearch.set(true);
    }, { injector: options.injector });
  };

  if (options.renderTrigger) {
    effect(() => {
      options.renderTrigger?.();
      if (isActive()) {
        scheduleRender(false);
      }
    }, { injector: options.injector });
  }

  if (options.remountTrigger) {
    effect(() => {
      options.remountTrigger?.();
      if (isActive()) {
        scheduleRender(true);
      }
    }, { injector: options.injector });
  }

  return {
    renderClientSearch,
    scheduleRender,
    destroy: () => {
      clearScheduledClientSearchRender();
      renderClientSearch.set(false);
      options.clearState();
    }
  };
}

export function createAuthorizedClientSearchRenderController(
  options: ClientSearchAuthorizationOptions
): ClientSearchRenderController {
  const hasStoreAuthorizedPlan = (): boolean => hasAuthorizedPlan(options.userAuths(), options.selectedAdminPlan());

  return createClientSearchRenderController({
    injector: options.injector,
    clearState: options.clearState,
    remountTrigger: options.remountTrigger,
    renderTrigger: options.userAuths,
    isActive: options.isActive,
    canRender: () => hasStoreAuthorizedPlan()
      && (options.canRenderWhenAuthorized?.() ?? true)
  });
}

export function createManagedClientSearchController(
  options: ManagedClientSearchOptions
): ManagedClientSearchController {
  const controller = createAuthorizedClientSearchRenderController({
    ...options,
    clearState: createClientSearchStateClearer(options.lux, options.storagePrefix, options.selectedAdminPlan)
  });

  return {
    ...controller,
    clientSearchRenderState: computed(() => options.clientSearchFetching() ? 'fetching' : 'idle')
  };
}

export function createFeatureManagedClientSearchController(
  options: FeatureManagedClientSearchOptions
): ManagedClientSearchController {
  const injector = inject(Injector);
  const clientUserAuth = inject(ClientUserAuthConfig);
  const signalScope = `${options.storagePrefix}client-search`;
  const userAuths = toSignal(options.lux.get(getUserAuths, `${signalScope}-auths`), {
    injector,
    initialValue: {} as Record<string, string[]>
  });
  const clientSearchFetching = toSignal(
    options.lux.get(
      state => (state as { clientSearch?: { isFetchingClient?: boolean } }).clientSearch?.isFetchingClient ?? false,
      `${signalScope}-fetching`
    ),
    {
      injector,
      initialValue: false
    }
  );

  effect(() => {
    clientUserAuth.userAuthorizations = userAuths();
  }, { injector });

  return createManagedClientSearchController({
    ...options,
    injector,
    userAuths,
    clientSearchFetching
  });
}
